import { Disclosure, Transition } from "@headlessui/react";
import { MinusIcon, PlusIcon, RectangleStackIcon } from "@heroicons/react/24/solid";
import React, { useEffect } from "react";

import { Button, EditableTextInput, TextArea } from "@fronterahealth/frontera-ui-components";

import NoTargetEmptyCard from "@components/TreatmentPlan/GoalPanel/NoTargetEmptyCard";
import { GoalItemType, TargetItem } from "@components/TreatmentPlan/utils";

export interface GoalDetailsPanelProps {
  goalIndex: number;
  goal: GoalItemType;
  defaultOpenState?: boolean;
  hideHeaderIcon?: boolean;
  handleAddTarget?: (goalIndex: number) => void;
  handleTargetChange: (goalIndex: number, targetIndex: number, updatedTarget: Partial<TargetItem>) => void;
  handleDeleteTarget: (goalIndex: number, targetIndex: number) => void;
  handleGoalChange: (goalIndex: number, updatedGoal: Partial<GoalItemType>) => void;
  handleDeleteGoal: (goalIndex: number) => void;
}

interface GoalTargetPanelProps {
  goalIndex: number;
  targetIndex: number;
  target: TargetItem;
  defaultOpenState?: boolean;
  hideHeaderIcon?: boolean;
  handleTargetChange: (goalIndex: number, targetIndex: number, updatedTarget: Partial<TargetItem>) => void;
  handleDeleteTarget: (goalIndex: number, targetIndex: number) => void;
}

const ConcentricCirclesIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className={className ?? "h-6 w-6"}
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
};
const textAreaClass =
  "disabled:bg-white border-limestone-100 rounded-md block w-full border-0 py-1.5 text-text-primary shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 read-only:resize-none read-only:bg-limestone-50 focus:ring-2 focus:ring-inset focus:ring-hatch-600 focus:invalid:border-pink-500 focus:invalid:ring-red-500 invalid:focus:ring-red-300 sm:text-sm sm:leading-6 ";
const commonTextAreaProps = {
  rows: 2,
  className: textAreaClass,
  formNoValidate: true,
};

const GoalTargetPanel: React.FC<GoalTargetPanelProps> = ({
  goalIndex,
  targetIndex,
  target,
  defaultOpenState = false,
  hideHeaderIcon,
  handleTargetChange,
  handleDeleteTarget,
}) => {
  const [open, setOpen] = React.useState<boolean>(defaultOpenState);
  useEffect(() => {
    setOpen(defaultOpenState);
  }, [defaultOpenState]);

  return (
    <div className="relative my-1 ml-4 flex w-full items-center rounded-md bg-limestone-100">
      <Disclosure defaultOpen={defaultOpenState}>
        {({}) => (
          <div className="flex w-full flex-col">
            <div className="relative flex w-full items-center gap-x-2 rounded-md bg-limestone-100 pl-6 py-2">
              {hideHeaderIcon ? null : (
                <>
                  <div className="flex flex-col items-center">
                    <ConcentricCirclesIcon className="h-5 w-5 text-text-secondary" />
                  </div>
                </>
              )}

              <EditableTextInput
                name={target.targetName}
                handleDelete={() => handleDeleteTarget(goalIndex, targetIndex)}
                handleEdit={(newValue) => handleTargetChange(goalIndex, targetIndex, { targetName: newValue })}
                className="bg-transparent! w-full"
                isInputBordered
              />
              <div className="absolute -left-3 h-6 w-6 ">
                {open ? (
                  <MinusIcon
                    onClick={() => setOpen((o) => !o)}
                    className={`absolute h-6 w-6 rounded-full border border-limestone-100 bg-white p-1 text-text-primary hover:cursor-pointer hover:bg-limestone-50`}
                  />
                ) : (
                  <PlusIcon
                    onClick={() => setOpen((o) => !o)}
                    className="absolute h-6 w-6 rounded-full border border-limestone-100 bg-white p-1 text-text-primary hover:cursor-pointer hover:bg-limestone-50"
                  />
                )}
              </div>
            </div>

            <Transition
              as="div"
              show={open}
              className="overflow-hidden"
              enter="transition transition-[max-height] duration-500 ease-in"
              enterFrom="transform max-h-0"
              enterTo="transform max-h-screen"
              leave="transition transition-[max-height] duration-500 ease-out"
              leaveFrom="transform max-h-screen"
              leaveTo="transform max-h-0"
            >
              <Disclosure.Panel static>
                <div className="flex flex-col gap-y-2 border-l border-limestone-100 bg-white py-4 pl-8">
                  <div>
                    <TextArea
                      label="Target Details"
                      value={target.targetDescription}
                      {...commonTextAreaProps}
                      onChange={(e) =>
                        handleTargetChange(goalIndex, targetIndex, { targetDescription: e.target.value })
                      }
                    />
                  </div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    </div>
  );
};

export const GoalDetailsPanel: React.FC<GoalDetailsPanelProps> = ({
  goal,
  goalIndex,
  defaultOpenState,
  hideHeaderIcon = false,
  handleAddTarget,
  handleTargetChange,
  handleDeleteTarget,
  handleGoalChange,
  handleDeleteGoal,
}) => {
  return (
    <div className="w-full flex flex-col rounded-2xl bg-white border border-limestone-100 pb-8">
      <div className="relative flex w-full items-center gap-x-2  border-b border-limestone-100 px-6 py-2">
        <div className="flex flex-col items-center">
          <RectangleStackIcon className="h-4 w-4 text-text-secondary" />
        </div>

        <EditableTextInput
          name={goal.goalName}
          handleDelete={() => handleDeleteGoal(goalIndex)}
          handleEdit={(newValue) => handleGoalChange(goalIndex, { goalName: newValue })}
          className="bg-transparent! w-full"
          isInputBordered
        />
      </div>
      <div className="flex flex-col gap-y-2 border-l border-limestone-50 bg-white py-4 px-8 ">
        <div>
          <TextArea
            label="Goal Details"
            value={goal.goalDescription}
            {...commonTextAreaProps}
            onChange={(e) => handleGoalChange(goalIndex, { goalDescription: e.target.value })}
          />
        </div>
      </div>
      {goal.targets?.length ? (
        <div className="flex flex-col m-8 pt-4 pr-3 border-l border-limestone-100">
          {goal.targets?.map((item, index) => {
            return (
              <GoalTargetPanel
                key={`${goal.goalName}${index}`}
                goalIndex={goalIndex}
                targetIndex={index}
                target={item}
                defaultOpenState={defaultOpenState}
                hideHeaderIcon={hideHeaderIcon}
                handleTargetChange={handleTargetChange}
                handleDeleteTarget={handleDeleteTarget}
              />
            );
          })}
        </div>
      ) : null}
      <div className="mx-6">
        <Button
          appearance="link"
          className="no-underline p-0!"
          text={
            <div className="flex flex-row items-center">
              <PlusIcon className="h-4 w-5" /> Add a target
            </div>
          }
          onClick={() => handleAddTarget?.(goalIndex)}
        />
      </div>
      {goal.targets?.length === 0 ? (
        <div className="px-6">
          <NoTargetEmptyCard
            title="No targets yet (optional)"
            description="Targets are optional, but help give our system more context to surface smarter insights."
          />
        </div>
      ) : null}
    </div>
  );
};
